import Typography from '~/shared/components/Typography/Typography';
import styles from './Card.module.scss';
import { Button, Link } from '~/shared/components';
import clsx from 'clsx';
import { ButtonColorProps, ContentCard } from '~/lib/data-contract/auto-generated/api-model';
import { useMemo } from 'react';
import { DynamicImageLoader } from '~/shared/components/DynamicImageLoader';
import { Watermark } from '~/shared/components/Watermarks';

type CardProp = ContentCard & {
    cardTitleSize?: 'Large' | 'Medium' | 'Small';
    /** True when the row has 3 or more cards, which makes each card narrow. */
    isDenseRow?: boolean;
    className?: string;
    imageRatio?: 'square' | 'rectangle';
    isTextLimit?: boolean;
    /** When true, card image uses Next/Image priority (eager) for above-the-fold rows. */
    imagePriority?: boolean;
};

const Card = ({
    className,
    imageRatio,
    imageOnBackground,
    header,
    trumpet,
    paragraph,
    link,
    linkType,
    image,
    imageAltText,
    alias,
    cardTrumpet,
    cardTitle,
    cardTitleSize = 'Large',
    isDenseRow,
    cardText,
    watermark,
    isTextLimit,
    imagePriority,
}: CardProp) => {
    const variantLink = useMemo(() => {
        let linkVariant: ButtonColorProps = 'new-link';

        if (linkType === 'Secondary') linkVariant = 'new-secondary';
        else if (linkType === 'Primary') linkVariant = 'primary';

        return linkVariant;
    }, [linkType]);

    const isImageOnBackground = imageOnBackground || alias === 'contentCardText';

    const renderWaterMark = () => {
        if (!watermark) return null;

        return (
            <Watermark
                type={watermark}
                styles={{
                    watermark: styles.waterMark,
                    watermarkLogo: styles.logo,
                }}
            />
        );
    };

    const imageField = useMemo(() => {
        const imageContent = (
            <div
                className={clsx(styles.imageContainer, {
                    [styles.imageOnBackground]: isImageOnBackground,
                    [styles.square]: imageRatio === 'square',
                    [styles.rectangle]: imageRatio === 'rectangle',
                    [styles.titleMedium]: cardTitleSize === 'Medium',
                    [styles.titleSmall]: cardTitleSize === 'Small',
                    [styles.denseRow]: isDenseRow,
                })}
            >
                {isImageOnBackground && renderWaterMark()}

                {image?.src && (
                    <DynamicImageLoader
                        className={styles.image}
                        src={image?.src ?? ''}
                        width={0}
                        height={0}
                        sizes="100vw"
                        alt={imageAltText ?? ''}
                        priority={imagePriority}
                    />
                )}

                {alias === 'contentCardText' && (
                    <div className={styles.cardText}>
                        {cardTrumpet && (
                            <Typography variant="trumpet-XL" as="p" className={styles.cardTrumpet}>
                                {cardTrumpet}
                            </Typography>
                        )}

                        {cardTitle && (
                            <Typography variant="display-XL" as="p" className={styles.cardTitle}>
                                {cardTitle}
                            </Typography>
                        )}

                        {cardText && (
                            <Typography
                                variant={cardTitle ? 'title-XS' : 'title-M'}
                                as="p"
                                className={styles.cardSubtitle}
                            >
                                {cardText}
                            </Typography>
                        )}
                    </div>
                )}
            </div>
        );

        if (link?.url) {
            return (
                <Link
                    title={link?.title}
                    noHover
                    href={link?.url}
                    target={link?.target}
                    className={styles.link}
                >
                    {imageContent}
                </Link>
            );
        }

        return imageContent;
    }, [
        link,
        imageRatio,
        isImageOnBackground,
        image,
        imageAltText,
        imagePriority,
        alias,
        cardTrumpet,
        cardTitle,
        cardTitleSize,
        isDenseRow,
        cardText,
    ]);

    return (
        <div className={clsx(styles.component, className)}>
            {imageField}

            <div className={styles.textContainer}>
                {trumpet && (
                    <Typography as="p" variant="paragraph-S" className={styles.trumpet}>
                        {trumpet}
                    </Typography>
                )}

                {header && (
                    <Typography as="h3" variant="title-XS">
                        {header}
                    </Typography>
                )}

                {paragraph && (
                    <Typography
                        as="p"
                        variant="paragraph-M"
                        className={isTextLimit ? styles.paragraph : ''}
                    >
                        {paragraph}
                    </Typography>
                )}

                {link?.url && (
                    <p className={styles.button}>
                        <Button
                            as={Link}
                            aria-label="link"
                            variant={variantLink}
                            href={link.url}
                            target={link.target}
                            className="hoverShadowStyle"
                        >
                            {link.title}
                        </Button>
                    </p>
                )}
            </div>
        </div>
    );
};

export default Card;
